
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Legal = () => {
  const faqs = [
    {
      question: "What is UAE copyright law and how does it apply to music?",
      answer: "UAE copyright law (Federal Law No. 7 of 2002 concerning Copyrights and Related Rights, as amended) protects original musical works, giving creators exclusive rights to control how their work is used. This includes the right to reproduce, distribute, perform, and license their music. Public performance of music in commercial settings requires proper licensing."
    },
    {
      question: "Who needs a music license in the UAE?",
      answer: "Any business that plays music in public areas needs a license. This includes cafés, restaurants, retail shops, malls, hotels, gyms, salons, and other venues where music is played. Even if you're using streaming services like Spotify or Apple Music, their personal-use licenses don't cover commercial use."
    },
    {
      question: "What happens if my business doesn't obtain proper music licensing?",
      answer: "Operating without proper music licenses can result in legal penalties including fines starting from 10,000 AED, potential business closure for repeat violations, and damage to your brand reputation. Additionally, individual rights holders may pursue separate claims for compensation."
    },
    {
      question: "How are royalties distributed to music creators?",
      answer: "When businesses obtain licenses through Music Nation, usage fees are collected and distributed to rights holders based on reported usage and our distribution framework. We utilize advanced tracking technology and reporting systems to ensure fair compensation for creators based on how often their music is played."
    },
    {
      question: "How long does a music license last and how do I renew it?",
      answer: "Standard business licenses are valid for one year from the date of issue. The renewal process begins 30 days before expiration through your Music Nation business dashboard, where you can review terms, update venue information if needed, and process payment to maintain continuous compliance."
    }
  ];

  return (
    <div className="min-h-screen bg-musicBlack">
      <Header />
      <main className="pt-24">
        {/* Hero section */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-r from-musicBlack via-musicBlack/90 to-musicBlack/80"></div>
            <img 
              src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5" 
              alt="Legal documents" 
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="relative z-10 editorial-container">
            <div className="max-w-2xl">
              <h1 className="text-white editorial-heading mb-4">Legal Hub</h1>
              <h2 className="editorial-subheading text-musicRed">UAE Copyright Overview & Resources</h2>
              <p className="text-white editorial-body mb-8">
                Navigate UAE music rights law with our comprehensive resources, designed to help both 
                creators and businesses understand their legal rights and obligations.
              </p>
              <Button className="cta-button" size="lg">Download Legal Guide</Button>
            </div>
          </div>
        </section>
        
        {/* UAE Copyright Overview */}
        <section className="py-24 bg-black">
          <div className="editorial-container">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <h2 className="text-white editorial-heading mb-6">UAE Copyright Framework</h2>
                <div className="space-y-6">
                  <p className="text-white editorial-body">
                    The United Arab Emirates has established a robust legal framework to protect intellectual property 
                    rights, including musical works. The primary legislation governing music rights is Federal Law No. 7 
                    of 2002 concerning Copyrights and Related Rights (as amended by Federal Law No. 32 of 2006).
                  </p>
                  <p className="text-white editorial-body">
                    This framework aligns with international standards, including the Berne Convention for the Protection 
                    of Literary and Artistic Works and the WIPO Copyright Treaty, providing creators with exclusive rights 
                    to control how their work is used and ensuring they receive fair compensation.
                  </p>
                  <p className="text-white editorial-body">
                    Music Nation serves as the official platform authorized by UAE authorities to facilitate compliance 
                    with these laws, providing a streamlined system for both creators and businesses to manage music rights.
                  </p>
                  
                  <div className="mt-8">
                    <h3 className="text-white font-serif text-xl font-bold mb-4">Key Legal Provisions:</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-musicRed font-bold mr-2">•</span>
                        <span className="text-musicGrey">
                          <span className="text-musicWhite font-medium">Duration of Protection:</span> Copyright for musical works extends throughout the author's lifetime plus 50 years after their death.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-musicRed font-bold mr-2">•</span>
                        <span className="text-musicGrey">
                          <span className="text-musicWhite font-medium">Public Performance Rights:</span> Any public performance of music requires proper licensing from rights holders.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-musicRed font-bold mr-2">•</span>
                        <span className="text-musicGrey">
                          <span className="text-musicWhite font-medium">Enforcement:</span> Rights holders can seek judicial remedies including injunctions, damages, and criminal penalties for infringement.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-musicRed font-bold mr-2">•</span>
                        <span className="text-musicGrey">
                          <span className="text-musicWhite font-medium">Moral Rights:</span> Creators maintain the right to be identified as the author and to object to distortions of their work.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="lg:col-span-1">
                <div className="bg-musicBlack border border-musicGrey/20 p-8 rounded-lg sticky top-28">
                  <h3 className="font-serif text-xl font-bold mb-6">Legal Resources</h3>
                  <ul className="space-y-4">
                    <li>
                      <a href="#" className="flex items-center text-musicWhite hover:text-musicRed transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-musicRed" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2H2v10h10V2z" />
                          <path d="M12 2h10v10H12V2z" />
                          <path d="M12 12H2v10h10V12z" />
                          <path d="M12 12h10v10H12V12z" />
                        </svg>
                        UAE Copyright Law (Full Text)
                      </a>
                    </li>
                    <li>
                      <a href="#" className="flex items-center text-musicWhite hover:text-musicRed transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-musicRed" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        Creator Rights Guide (PDF)
                      </a>
                    </li>
                    <li>
                      <a href="#" className="flex items-center text-musicWhite hover:text-musicRed transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-musicRed" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        Business Compliance Guide (PDF)
                      </a>
                    </li>
                    <li>
                      <a href="#" className="flex items-center text-musicWhite hover:text-musicRed transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-musicRed" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                          <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
                          <path d="M12 11h4" />
                          <path d="M12 16h4" />
                          <path d="M8 11h.01" />
                          <path d="M8 16h.01" />
                        </svg>
                        Sample License Agreements
                      </a>
                    </li>
                    <li>
                      <a href="#" className="flex items-center text-musicWhite hover:text-musicRed transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-musicRed" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 2H2v16" />
                          <path d="M22 22H8V8" />
                        </svg>
                        Royalty Distribution Framework
                      </a>
                    </li>
                  </ul>
                  
                  <div className="mt-8 pt-6 border-t border-musicGrey/20">
                    <Button className="secondary-button w-full">Request Legal Consultation</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQs section */}
        <section className="py-24 bg-gradient-to-b from-black to-musicBlack">
          <div className="editorial-container">
            <h2 className="text-white editorial-heading text-center mb-16">Frequently Asked Questions</h2>
            
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border-musicGrey/20">
                    <AccordionTrigger className="font-serif text-lg font-medium text-musicWhite hover:text-musicRed">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-musicGrey pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
        
        {/* CTA section */}
        <section className="py-16 bg-black">
          <div className="editorial-container">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-white font-serif text-2xl md:text-3xl font-bold mb-4">Have Specific Legal Questions?</h2>
              <p className="text-musicGrey mb-8 max-w-2xl mx-auto">
                Our team of legal experts specializing in UAE copyright law can provide guidance tailored to your situation.
              </p>
              <div className="flex flex-col md:flex-row justify-center gap-4">
                <Button className="cta-button" size="lg">Contact Legal Team</Button>
                <Button variant="outline" className="text-white secondary-button" size="lg">Schedule Consultation</Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Legal;
